import type { ButtonHTMLAttributes, ReactNode, Ref } from 'react';
import { Link } from 'react-router';

import type { TestIdProps } from '../../testing';
import styles from './Button.module.css';

export type ButtonVariant = 'primary' | 'ghost' | 'solid';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className' | 'children'>, TestIdProps {
  variant?: ButtonVariant | undefined;
  size?: ButtonSize | undefined;
  className?: string | undefined;
  children?: ReactNode;
  linkTo?: string | undefined;
  /** Navigation state forwarded to the underlying router Link (only when linkTo is set). */
  linkState?: unknown;
  /** Forwarded to the native button (ignored when linkTo is set), e.g. for focus management. */
  ref?: Ref<HTMLButtonElement> | undefined;
}

export function Button({
  variant = 'primary',
  size = 'md',
  className,
  children,
  linkTo,
  linkState,
  type = 'button',
  ref,
  'data-testid': testId,
  ...rest
}: ButtonProps) {
  const classes = [styles['button'], className].filter(Boolean).join(' ');

  if (linkTo !== undefined) {
    return (
      <Link
        to={linkTo}
        state={linkState}
        className={classes}
        data-variant={variant}
        data-size={size}
        data-testid={testId}
      >
        <span className={styles['label']}>{children}</span>
      </Link>
    );
  }

  return (
    <button
      {...rest}
      ref={ref}
      type={type}
      className={classes}
      data-variant={variant}
      data-size={size}
      data-testid={testId}
    >
      <span className={styles['label']}>{children}</span>
    </button>
  );
}
